import { booleanColumn, floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { useClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import { PurchaseOrderReceptionApi } from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderReceptionApi.ts";
import { PurchaseOrderReceiptLineView } from "src/api/generated/erp/db/types/tables/purchaseOrderReceiptLineView";
import { RefreshableElementProps, RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { PurchaseOrderReceipt } from "src/api/generated/erp/db/types/tables/purchaseOrderReceipt.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PurchaseOrderReceiptLinesDataGridProps extends RefreshableElementProps {
	purchaseOrderReceipt: PurchaseOrderReceipt;
	editEnabled?: boolean;
	purchaseOrderLinesToReceiveViewRefreshRef?: RefreshableElementRef;
	/** Read-only in the purchase order view, which reads receipts with the purchase order read permission. */
	purchaseOrderView?: boolean;
}

export const PurchaseOrderReceiptLinesDataGrid = ({
	purchaseOrderReceipt,
	editEnabled = false,
	refreshRef,
	purchaseOrderLinesToReceiveViewRefreshRef,
	purchaseOrderView = false,
}: PurchaseOrderReceiptLinesDataGridProps) => {
	const { dataGridProps } = useClientSideDataGridModel({
		customFunctionUiActionView: "PURCHASE_ORDER_RECEIPT_LINES",
		fetchData: () =>
			purchaseOrderView ?
				PurchaseOrderReceptionApi.getPurchaseOrderReceiptLinesForPurchaseOrderView({
					purchaseOrderReceiptId: purchaseOrderReceipt.purchaseOrderReceiptId,
				})
			:	PurchaseOrderReceptionApi.getPurchaseOrderReceiptLines({
					purchaseOrderReceiptId: purchaseOrderReceipt.purchaseOrderReceiptId,
				}),
		getRowId: (row) => row.purchaseOrderReceiptLineId,
		gridId: "752688624A18A965",
		initialParams: {},
		refreshRef: refreshRef,
	});

	return (
		<ControlledAsyncFormCrudDataGrid<PurchaseOrderReceiptLineView>
			checkboxSelection
			columns={[
				textColumn({
					field: "partWarehouseLocationCode",
					headerName: i18n.t("warehouse_location"),
				}),
				{
					field: "configurationOrPartDescription",
					headerName: i18n.t("part"),
					width: 200,
				},
				floatColumn({
					field: "receivedQuantityInPurchaseUnit",
					headerName: i18n.t("received_quantity"),
					width: 120,
				}),
				textColumn({
					field: "purchaseUnit",
					headerName: i18n.t("purchase_unit"),
					width: 120,
				}),
				booleanColumn({
					field: "fullyReceived",
					headerName: i18n.t("fully_received"),
					width: 140,
				}),
				textColumn({
					field: "note",
					headerName: i18n.t("note"),
					width: 200,
				}),
				integerColumn({
					field: "purchaseOrderReceiptLineId",
					headerName: i18n.t("id"),
				}),
			]}
			remove={
				!editEnabled ? { type: "hidden" }
				: purchaseOrderReceipt.purchaseOrderReceiptState === "RECEIPTED" ?
					{
						type: "disabled",
						tooltip: i18n.t("receipt_is_already_marked_as_completed"),
					}
				:	{
						type: "enabled",
						action: async ({ items }) => {
							await PurchaseOrderReceptionApi.deletePurchaseOrderReceiptLines({
								purchaseOrderReceiptLineIds: items.map((item) => item.purchaseOrderReceiptLineId),
							});
							purchaseOrderLinesToReceiveViewRefreshRef?.refresh();
						},
					}

			}
			agentContext={{
				key: defineAgentUiContextKey("erp.purchase.purchaseOrderReceiptLines.grid"),
				data: {
					purchaseOrderId: purchaseOrderReceipt.purchaseOrderId,
					purchaseOrderReceiptId: purchaseOrderReceipt.purchaseOrderReceiptId,
				},
				rowFields: [
					"purchaseOrderReceiptLineId",
					"purchaseOrderReceiptId",
					"purchaseOrderLineId",
					"partId",
					"source",
					"sourceId",
				],
				selectedRowPathSegment: (row) => String(row.purchaseOrderReceiptLineId),
				selectedRowsPathSegment: (count) => i18n.t("purchase_order_receipt_lines_selected_count", { count }),
			}}
			{...dataGridProps}
		/>
	);
};
